import { getDatabase, ref, get, set, update, push, serverTimestamp, runTransaction } from "firebase/database";

/**
 * Community Utility Functions
 * Frontend helpers for interacting with the community RTDB nodes.
 */

const getDb = () => getDatabase();

// ===================== API FETCH HELPERS =====================
// Uses the Express backend for read operations that require filtering/sorting
// or write operations that require moderation/server-side logic.

const getAuthToken = () => {
  const cachedUser = localStorage.getItem("bbr.cachedUserDetails");
  if (cachedUser) {
    try {
      const parsed = JSON.parse(cachedUser);
      // In this app structure, token might be handled differently, 
      // but usually standard fetch requests include standard auth headers if available
      return parsed.token || null;
    } catch (e) {
      return null;
    }
  }
  return null;
};

const defaultHeaders = () => {
  const headers = { "Content-Type": "application/json" };
  // Note: The main app uses a specific auth context for tokens, 
  // these utility functions should ideally be passed the token from the component
  return headers;
};

// ===================== CLIENT-SIDE RTDB HELPERS =====================
// For fast, optimistic client-side updates or simple reads.

export const fetchCommunityProfile = async (userId) => {
  try {
    const db = getDb();
    const profileRef = ref(db, `communityProfiles/${userId}`);
    const snapshot = await get(profileRef);
    if (snapshot.exists()) {
      return snapshot.val();
    }
    return null;
  } catch (error) {
    console.error("Error fetching community profile:", error);
    return null;
  }
};

export const hasCommunityRole = async (userId, roleName) => {
  const profile = await fetchCommunityProfile(userId);
  if (!profile || !profile.communityRoles) return false;
  return profile.communityRoles.includes(roleName);
};

export const formatTimeAgo = (timestamp) => {
  if (!timestamp) return "";
  
  const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);
  
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + "y ago";
  
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + "mo ago";
  
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + "d ago";
  
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + "h ago";
  
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + "m ago";
  
  return "Just now";
};

export const stripHtml = (html) => {
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

export const getTrustLevelColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'bronze': return '#cd7f32';
    case 'silver': return '#c0c0c0';
    case 'gold': return '#ffd700';
    case 'platinum': return '#e5e4e2';
    default: return '#6c757d';
  }
};
